// src/pages/promotions/modals/RankModal.jsx
import { ReusableModal } from "@/components/UI/Modals/CustomModals";
import { Table, Button, Dropdown, message, Spin } from "antd";
import {
  MoreOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useState } from "react";
import RankForm from "./RankForm"; // New form component (below)
import {
  useGetRanksQuery,
  useDeleteRankMutation,
} from "@/services/api/promo/promoApi";

const RankModal = ({ open, onClose }) => {
  const [formMode, setFormMode] = useState(null); // "add" | "edit"
  const [editingRank, setEditingRank] = useState(null);

  const {
    data: ranks = [],
    isLoading,
    refetch,
  } = useGetRanksQuery({ skip: !open });
  const [deleteRank] = useDeleteRankMutation();

  const handleAdd = () => {
    setFormMode("add");
    setEditingRank(null);
  };

  const handleEdit = (record) => {
    setFormMode("edit");
    setEditingRank(record); // This triggers useEffect in RankForm
  };

  // Optional: Reset on modal close
  const handleFormClose = () => {
    setFormMode(null);
    setEditingRank(null);
    // onClose();
  };

  const handleDelete = async (id) => {
    try {
      await deleteRank(id).unwrap();
      message.success("Rank deleted");
    } catch {
      message.error("Failed to delete rank");
    }
  };

  const handleFormSuccess = () => {
    setFormMode(null);
    setEditingRank(null);
    refetch();
  };

  const columns = [
    {
      title: "S/N",
      key: "sn",
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Rank Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Qualifying Order Amount",
      dataIndex: "qualifying_order_no",
      key: "qualifying_order_no",
      render: (val) => `${val} Orders`,
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "edit",
                label: "Edit",
                icon: <EditOutlined />,
                onClick: () => handleEdit(record),
              },
              {
                key: "delete",
                label: "Delete",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => handleDelete(record.id),
              },
            ],
          }}
          trigger={["click"]}
        >
          <Button
            type="text"
            icon={<MoreOutlined style={{ fontSize: "20px" }} />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Ranks"
      subtitle="Kindly see all customer ranks details below"
      position="right"
      slideDirection="right"
      animationDuration={400}
      width={700}
      customFooter
      footer={
        <div className="flex justify-end">
          <Button onClick={onClose} size="large">
            Close
          </Button>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin size="large" />
        </div>
      ) : (
        <>
          {/* Header */}
          <div className="flex justify-between mb-4">
            <h3 className="font-medium text-[#262626]">All Ranks</h3>
            <Button
              type="default"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                borderColor: "#faad14",
                color: "#faad14",
                fontWeight: 500,
              }}
            >
              Add Rank
            </Button>
          </div>

          {/* Table */}
          <Table
            columns={columns}
            dataSource={ranks}
            pagination={false}
            bordered={false}
            rowKey="id"
            className="custom-table"
          />

          {/* Inline Form Modal */}
          <RankForm
            open={!!formMode}
            onClose={handleFormClose}
            mode={formMode}
            initialData={editingRank}
            onSuccess={handleFormSuccess}
          />
        </>
      )}
    </ReusableModal>
  );
};

export default RankModal;
